import { useState, useMemo } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { useToast } from "@/hooks/use-toast";
import {
  Plus,
  Search,
  Edit,
  Trash2,
  FolderTree,
  Filter,
  RefreshCw,
  CheckCircle,
  XCircle,
  Eye,
  Package,
  ImagePlus,
} from "lucide-react";
import { 
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { categoryService, Category, CategoryFilters, CategoryDetails, CreateCategoryRequest, UpdateCategoryRequest } from "@/services/categoryService";

export default function Categories() {
  const { t, i18n } = useTranslation();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  
  // State management
  const [search, setSearch] = useState('');
  const [isActive, setIsActive] = useState<string>('all');
  const [currentPage, setCurrentPage] = useState(1);
  const [limit, setLimit] = useState(50);
  const [isViewDialogOpen, setIsViewDialogOpen] = useState(false);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState<Category | null>(null);
  const [editFormData, setEditFormData] = useState<UpdateCategoryRequest>({});
  const [createFormData, setCreateFormData] = useState<CreateCategoryRequest>({ name: '', name_ar: '' });
  const [createImgUploading, setCreateImgUploading] = useState(false);
  const [editImgUploading, setEditImgUploading] = useState(false);

  // Build filters object for API
  const filters: CategoryFilters = useMemo(() => {
    const filterObj: CategoryFilters = {
      page: currentPage,
      limit: limit
    };
    if (search) filterObj.q = search;
    if (isActive !== 'all') filterObj.is_active = isActive;
    return filterObj;
  }, [currentPage, limit, search, isActive]);

  // React Query for categories data
  const { data: categoriesResponse, isLoading: categoriesLoading, error: categoriesError, refetch: refetchCategories } = useQuery({
    queryKey: ['categories', filters],
    queryFn: () => categoryService.getCategories(filters),
    staleTime: 30000, // 30 seconds
  });

  // Extract categories and pagination from response
  const categories = useMemo(() => {
    return categoriesResponse?.success && categoriesResponse?.data ? categoriesResponse.data.items : [];
  }, [categoriesResponse]);

  const pagination = useMemo(() => {
    return categoriesResponse?.success && categoriesResponse?.data ? {
      total: categoriesResponse.data.total,
      totalPages: categoriesResponse.data.totalPages,
      currentPage: categoriesResponse.data.page,
      perPage: categoriesResponse.data.pageSize
    } : {
      total: 0,
      totalPages: 0,
      currentPage: 1,
      perPage: 50
    };
  }, [categoriesResponse]);

  // React Query for category statistics
  const { data: statsResponse, isLoading: statsLoading } = useQuery({
    queryKey: ['categoryStats'],
    queryFn: () => categoryService.getCategoryStats(),
    staleTime: 60000, // 1 minute
  });

  const categoryStats = useMemo(() => {
    return statsResponse?.success && statsResponse?.data ? statsResponse.data : {
      total: 0,
      active: 0,
      inactive: 0
    };
  }, [statsResponse]);

  const isLoading = categoriesLoading || statsLoading;

  // React Query for category details
  const { data: categoryDetailsResponse, isLoading: detailsLoading } = useQuery({
    queryKey: ['categoryDetails', selectedCategory?.id],
    queryFn: () => categoryService.getCategoryById(selectedCategory!.id),
    enabled: !!selectedCategory && isViewDialogOpen,
    staleTime: 30000,
  });

  const categoryDetails = useMemo(() => {
    return categoryDetailsResponse?.success && categoryDetailsResponse?.data 
      ? categoryDetailsResponse.data 
      : null;
  }, [categoryDetailsResponse]);

  // Mutations
  const updateCategoryMutation = useMutation({
    mutationFn: ({ id, data }: { id: number; data: UpdateCategoryRequest }) =>
      categoryService.updateCategory(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['categories'] });
      queryClient.invalidateQueries({ queryKey: ['categoryStats'] });
      queryClient.invalidateQueries({ queryKey: ['categoryDetails'] });
      setIsEditDialogOpen(false);
      setEditFormData({});
      setSelectedCategory(null);
      toast({
        title: t("success") || "Success",
        description: t("categoryUpdated") || "Category updated successfully",
      });
    },
    onError: (error: any) => {
      toast({
        title: t("error") || "Error",
        description: error?.message || t("failedToUpdateCategory") || "Failed to update category",
        variant: "destructive",
      });
    },
  });

  const deleteCategoryMutation = useMutation({
    mutationFn: (id: number) => categoryService.deleteCategory(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['categories'] });
      queryClient.invalidateQueries({ queryKey: ['categoryStats'] });
      toast({
        title: t("success") || "Success",
        description: t("categoryDeleted") || "Category deleted successfully",
      });
    },
    onError: (error: any) => {
      toast({
        title: t("error") || "Error",
        description: error?.message || t("failedToDeleteCategory") || "Failed to delete category",
        variant: "destructive",
      });
    },
  });

  const createCategoryMutation = useMutation({
    mutationFn: (data: CreateCategoryRequest) => categoryService.createCategory(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['categories'] });
      queryClient.invalidateQueries({ queryKey: ['categoryStats'] });
      setIsCreateDialogOpen(false);
      setCreateFormData({ name: '', name_ar: '' });
      toast({
        title: t("success") || "Success",
        description: t("categoryCreated") || "Category created successfully",
      });
    },
    onError: (error: any) => {
      toast({
        title: t("error") || "Error",
        description: error?.message || t("failedToCreateCategory") || "Failed to create category",
        variant: "destructive",
      });
    },
  });

  // Handle filter changes - reset to page 1 when filters change
  const handleSearchChange = (value: string) => {
    setSearch(value);
    setCurrentPage(1);
  };

  const handleStatusChange = (value: string) => {
    setIsActive(value);
    setCurrentPage(1);
  };

  // Event handlers
  const handleViewCategory = (category: Category) => {
    setSelectedCategory(category);
    setIsViewDialogOpen(true);
  };

  const handleEditCategory = (category: Category) => {
    setSelectedCategory(category);
    setEditFormData({
      name: category.name,
      name_ar: category.name_ar,
      icon: category.icon,
      is_active: category.is_active,
    });
    setIsEditDialogOpen(true);
  };

  const handleSaveEdit = () => {
    if (!selectedCategory) return;
    updateCategoryMutation.mutate({ id: selectedCategory.id, data: editFormData });
  };

  const handleDeleteCategory = (category: Category) => {
    const confirmMessage = t("confirmDeleteCategory") || `Are you sure you want to delete category "${category.name}"?`;
    if (confirm(confirmMessage)) {
      deleteCategoryMutation.mutate(category.id);
    }
  };

  const handleCreateCategory = () => {
    if (!createFormData.name || !createFormData.name_ar) {
      toast({
        title: t("error") || "Error",
        description: t("categoryNameRequired") || "Category name (EN and AR) are required",
        variant: "destructive",
      });
      return;
    }
    createCategoryMutation.mutate(createFormData);
  };

  const uploadCategoryImage = async (
    file: File,
    setUploading: (v: boolean) => void,
    onUrl: (url: string) => void
  ) => {
    setUploading(true);
    try {
      const res = await categoryService.uploadImage(file);
      if (res.success && res.data?.url) {
        onUrl(res.data.url);
      } else {
        toast({ title: t("error") || "Error", description: res.message || "Upload failed", variant: "destructive" });
      }
    } catch (e: any) {
      toast({ title: t("error") || "Error", description: e?.message || "Upload failed", variant: "destructive" });
    } finally {
      setUploading(false);
    }
  };

  // Get display name based on current language
  const getDisplayName = (category: Category) => {
    return i18n.language === 'ar' ? category.name_ar : category.name;
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">{t("categoriesManage") || "Categories Manage"}</h1>
          <p className="text-muted-foreground mt-1">
            {t("organizeProductsCategories") || "Organize your products into categories"}
          </p>
        </div>
        <Button onClick={() => setIsCreateDialogOpen(true)}>
          <Plus className="mr-2 h-4 w-4" />
          {t("addCategory") || "Add Category"}
        </Button>
      </div>

      {/* Statistics Cards */}
      <div className="grid gap-4 md:grid-cols-3">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">{t("totalCategories") || "Total Categories"}</CardTitle>
            <FolderTree className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{isLoading ? "..." : categoryStats.total}</div>
          </CardContent>
        </Card>
        
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">{t("activeCategories") || "Active Categories"}</CardTitle>
            <CheckCircle className="h-4 w-4 text-green-600" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-green-600">{isLoading ? "..." : categoryStats.active}</div>
          </CardContent>
        </Card>
        
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">{t("inactiveCategories") || "Inactive Categories"}</CardTitle>
            <XCircle className="h-4 w-4 text-red-600" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-red-600">{isLoading ? "..." : categoryStats.inactive}</div>
          </CardContent>
        </Card>
      </div>

      {/* Search and Filters */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center">
            <Filter className="mr-2 h-5 w-5" />
            {t("searchAndFilters") || "Search and Filters"}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
            <div className="space-y-2">
              <Label htmlFor="search">{t("search") || "Search"}</Label>
              <div className="relative">
                <Search className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                <Input
                  id="search"
                  placeholder={t("searchCategoriesPlaceholder") || "Search by name..."}
                  value={search}
                  onChange={(e) => handleSearchChange(e.target.value)}
                  className="pl-10"
                />
              </div>
            </div>
            
            <div className="space-y-2">
              <Label>{t("status") || "Status"}</Label>
              <Select value={isActive || 'all'} onValueChange={handleStatusChange}>
                <SelectTrigger>
                  <SelectValue placeholder={t("selectStatus") || "Select Status"} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">{t("allStatuses") || "All Statuses"}</SelectItem>
                  <SelectItem value="true">{t("active") || "Active"}</SelectItem>
                  <SelectItem value="false">{t("inactive") || "Inactive"}</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            <div className="space-y-2">
              <Label>{t("pageSize") || "Items per page"}</Label>
              <Select value={limit.toString()} onValueChange={(value) => {
                setLimit(Math.min(parseInt(value), 100));
                setCurrentPage(1);
              }}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="10">10</SelectItem>
                  <SelectItem value="25">25</SelectItem>
                  <SelectItem value="50">50</SelectItem>
                  <SelectItem value="100">100</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Categories Table */}
      <Card>
        <CardHeader>
          <CardTitle>{t("categories") || "Categories"} ({isLoading ? "..." : pagination.total})</CardTitle>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>{t("id") || "ID"}</TableHead>
                <TableHead>{t("categoryName") || "Name"}</TableHead>
                <TableHead>{t("categoryNameAr") || "Name (AR)"}</TableHead>
                <TableHead>{t("categoryIcon") || "Icon"}</TableHead>
                <TableHead>{t("status") || "Status"}</TableHead>
                <TableHead>{t("createdAt") || "Created At"}</TableHead>
                <TableHead className="text-right">{t("actions") || "Actions"}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {isLoading ? (
                <TableRow>
                  <TableCell colSpan={7} className="text-center py-8">
                    <div className="flex items-center justify-center">
                      <RefreshCw className="mr-2 h-4 w-4 animate-spin" />
                      {t("loading")}...
                    </div>
                  </TableCell>
                </TableRow>
              ) : categories.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} className="text-center py-8">
                    <div className="text-muted-foreground">
                      {t("noCategoriesFound") || "No categories found"}
                    </div>
                  </TableCell>
                </TableRow>
              ) : (
                categories.map((category) => (
                  <TableRow key={category.id}>
                    <TableCell>{category.id}</TableCell>
                    <TableCell className="font-medium">{category.name}</TableCell>
                    <TableCell className="font-medium">{category.name_ar}</TableCell>
                    <TableCell>
                      <Badge variant="outline">{category.icon}</Badge>
                    </TableCell>
                    <TableCell>
                      <Badge variant={category.is_active ? "default" : "secondary"}>
                        {category.is_active ? t("active") || "Active" : t("inactive") || "Inactive"}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      {new Date(category.createdAt).toLocaleDateString()}
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-2">
                        <Button 
                          variant="ghost" 
                          size="icon" 
                          title={t("viewCategory") || "View Category"}
                          onClick={() => handleViewCategory(category)}
                        >
                          <Eye className="h-4 w-4" />
                        </Button>
                        <Button 
                          variant="ghost" 
                          size="icon" 
                          title={t("editCategory") || "Edit Category"}
                          onClick={() => handleEditCategory(category)}
                        >
                          <Edit className="h-4 w-4" />
                        </Button>
                        <Button 
                          variant="ghost" 
                          size="icon" 
                          title={t("deleteCategory") || "Delete Category"}
                          onClick={() => handleDeleteCategory(category)}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
          
          {/* Pagination */}
          {pagination.totalPages > 0 && (
            <div className="flex flex-col sm:flex-row items-center justify-between gap-3 py-4">
              <div className="text-sm text-muted-foreground text-center sm:text-left">
                {t("showing") || "Showing"} {(pagination.currentPage - 1) * pagination.perPage + 1}–{Math.min(pagination.currentPage * pagination.perPage, pagination.total)} {t("of") || "of"} {pagination.total} {t("categories") || "categories"}
              </div>

              <div className="flex items-center gap-1">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                  disabled={currentPage === 1 || isLoading}
                >
                  {t("previous") || "Prev"}
                </Button>

                <div className="hidden sm:flex items-center gap-1">
                  {Array.from({ length: Math.min(5, pagination.totalPages) }, (_, i) => {
                    let pageNum;
                    if (pagination.totalPages <= 5) {
                      pageNum = i + 1;
                    } else if (currentPage <= 3) {
                      pageNum = i + 1;
                    } else if (currentPage >= pagination.totalPages - 2) {
                      pageNum = pagination.totalPages - 4 + i;
                    } else {
                      pageNum = currentPage - 2 + i;
                    }
                    return (
                      <Button
                        key={pageNum}
                        variant={currentPage === pageNum ? "default" : "outline"}
                        size="sm"
                        onClick={() => setCurrentPage(pageNum)}
                        disabled={isLoading}
                        className="w-9"
                      >
                        {pageNum}
                      </Button>
                    );
                  })}
                </div>

                <span className="sm:hidden text-sm px-2 text-muted-foreground">
                  {currentPage} / {pagination.totalPages}
                </span>

                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setCurrentPage(Math.min(pagination.totalPages, currentPage + 1))}
                  disabled={currentPage === pagination.totalPages || isLoading}
                >
                  {t("next") || "Next"}
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Create Category Dialog */}
      <Dialog open={isCreateDialogOpen} onOpenChange={(open) => {
        setIsCreateDialogOpen(open);
        if (!open) setCreateFormData({ name: '', name_ar: '' });
      }}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>{t("addCategory") || "Add Category"}</DialogTitle>
            <DialogDescription>
              {t("createNewCategory") || "Create a new product category"}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="create-name">{t("categoryName") || "Name"} *</Label>
              <Input
                id="create-name"
                value={createFormData.name}
                onChange={(e) => setCreateFormData(prev => ({ ...prev, name: e.target.value }))}
                placeholder={t("enterCategoryName") || "Enter category name"}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="create-name-ar">{t("categoryNameAr") || "Name (AR)"} *</Label>
              <Input
                id="create-name-ar"
                value={createFormData.name_ar}
                onChange={(e) => setCreateFormData(prev => ({ ...prev, name_ar: e.target.value }))}
                placeholder={t("enterCategoryNameAr") || "Enter category name in Arabic"}
                dir="rtl"
              />
            </div>

            <div className="space-y-2">
              <Label>{t("categoryImage") || "Image"}</Label>
              <div className="flex items-center gap-3">
                {createFormData.icon && (
                  <img src={createFormData.icon} alt="preview" className="h-16 w-16 rounded object-cover border" />
                )}
                <label className="flex items-center gap-2 cursor-pointer px-4 py-2 border rounded-md hover:bg-muted transition-colors">
                  {createImgUploading ? (
                    <RefreshCw className="h-4 w-4 animate-spin" />
                  ) : (
                    <ImagePlus className="h-4 w-4" />
                  )}
                  <span className="text-sm">{createImgUploading ? (t("uploading") || "Uploading...") : (t("uploadImage") || "Upload Image")}</span>
                  <input
                    type="file"
                    accept="image/*"
                    className="hidden"
                    disabled={createImgUploading}
                    onChange={(e) => {
                      const file = e.target.files?.[0];
                      if (file) uploadCategoryImage(file, setCreateImgUploading, (url) => setCreateFormData(prev => ({ ...prev, icon: url })));
                      e.target.value = '';
                    }}
                  />
                </label>
                {createFormData.icon && (
                  <Button variant="ghost" size="sm" onClick={() => setCreateFormData(prev => ({ ...prev, icon: '' }))}>
                    {t("remove") || "Remove"}
                  </Button>
                )}
              </div>
            </div>

            <div className="space-y-2">
              <Label>{t("status") || "Status"}</Label>
              <Select
                value={createFormData.is_active !== false ? 'true' : 'false'}
                onValueChange={(value) => setCreateFormData(prev => ({ ...prev, is_active: value === 'true' }))}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="true">{t("active") || "Active"}</SelectItem>
                  <SelectItem value="false">{t("inactive") || "Inactive"}</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => {
              setIsCreateDialogOpen(false);
              setCreateFormData({ name: '', name_ar: '' });
            }}>
              {t("cancel") || "Cancel"}
            </Button>
            <Button
              onClick={handleCreateCategory}
              disabled={createCategoryMutation.isPending || createImgUploading}
            >
              {createCategoryMutation.isPending ? (
                <>
                  <RefreshCw className="mr-2 h-4 w-4 animate-spin" />
                  {t("creating") || "Creating..."}
                </>
              ) : (
                <>
                  <Plus className="mr-2 h-4 w-4" />
                  {t("createCategory") || "Create Category"}
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* View Category Dialog */}
      <Dialog open={isViewDialogOpen} onOpenChange={setIsViewDialogOpen}>
        <DialogContent className="max-w-3xl">
          <DialogHeader>
            <DialogTitle>{t("categoryDetails") || "Category Details"}</DialogTitle>
            <DialogDescription>
              {t("viewCategoryInformation") || "View detailed category information"}
            </DialogDescription>
          </DialogHeader>
          
          {categoryDetails ? (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>{t("categoryName") || "Name"}</Label>
                  <p className="text-sm font-medium">{categoryDetails.name}</p>
                </div>
                <div>
                  <Label>{t("categoryNameAr") || "Name (AR)"}</Label>
                  <p className="text-sm font-medium">{categoryDetails.name_ar}</p>
                </div>
                <div>
                  <Label>{t("categoryIcon") || "Icon"}</Label>
                  <Badge variant="outline">{categoryDetails.icon}</Badge>
                </div>
                <div>
                  <Label>{t("status") || "Status"}</Label>
                  <Badge variant={categoryDetails.is_active ? "default" : "secondary"}>
                    {categoryDetails.is_active ? t("active") || "Active" : t("inactive") || "Inactive"}
                  </Badge>
                </div>
                <div>
                  <Label>{t("createdAt") || "Created At"}</Label>
                  <p className="text-sm">{new Date(categoryDetails.createdAt).toLocaleString()}</p>
                </div>
                <div>
                  <Label>{t("updatedAt") || "Updated At"}</Label>
                  <p className="text-sm">{new Date(categoryDetails.updatedAt).toLocaleString()}</p>
                </div>
              </div>

              {categoryDetails.Products && categoryDetails.Products.length > 0 && (
                <div className="space-y-2">
                  <Label className="flex items-center">
                    <Package className="mr-2 h-4 w-4" />
                    {t("products") || "Products"} ({categoryDetails.Products.length})
                  </Label>
                  <div className="border rounded-lg">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>{t("productName") || "Product Name"}</TableHead>
                          <TableHead>{t("status") || "Status"}</TableHead>
                          <TableHead>{t("stock") || "Stock"}</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {categoryDetails.Products.map((product) => (
                          <TableRow key={product.id}>
                            <TableCell className="font-medium">{product.name}</TableCell>
                            <TableCell>
                              <Badge variant={product.status === "active" ? "default" : "secondary"}>
                                {product.status}
                              </Badge>
                            </TableCell>
                            <TableCell>{product.stock}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                </div>
              )}
            </div>
          ) : detailsLoading ? (
            <div className="flex items-center justify-center py-8">
              <RefreshCw className="mr-2 h-4 w-4 animate-spin" />
              {t("loading")}...
            </div>
          ) : null}
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsViewDialogOpen(false)}>
              {t("close") || "Close"}
            </Button>
            {selectedCategory && (
              <Button onClick={() => {
                setIsViewDialogOpen(false);
                handleEditCategory(selectedCategory);
              }}>
                <Edit className="mr-2 h-4 w-4" />
                {t("edit") || "Edit"}
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Category Dialog */}
      <Dialog open={isEditDialogOpen} onOpenChange={setIsEditDialogOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>{t("editCategory") || "Edit Category"}</DialogTitle>
            <DialogDescription>
              {t("updateCategoryInformation") || "Update category information"}
            </DialogDescription>
          </DialogHeader>

          {selectedCategory && (
            <div className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="edit-name">{t("categoryName") || "Name"} *</Label>
                <Input
                  id="edit-name"
                  value={editFormData.name || ''}
                  onChange={(e) => setEditFormData(prev => ({ ...prev, name: e.target.value }))}
                  placeholder={t("enterCategoryName") || "Enter category name"}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="edit-name-ar">{t("categoryNameAr") || "Name (AR)"} *</Label>
                <Input
                  id="edit-name-ar"
                  value={editFormData.name_ar || ''}
                  onChange={(e) => setEditFormData(prev => ({ ...prev, name_ar: e.target.value }))}
                  placeholder={t("enterCategoryNameAr") || "Enter category name in Arabic"}
                  dir="rtl"
                />
              </div>

              <div className="space-y-2">
                <Label>{t("categoryImage") || "Image"}</Label>
                <div className="flex items-center gap-3">
                  {editFormData.icon && editFormData.icon.startsWith('http') && (
                    <img src={editFormData.icon} alt="preview" className="h-16 w-16 rounded object-cover border" />
                  )}
                  <label className="flex items-center gap-2 cursor-pointer px-4 py-2 border rounded-md hover:bg-muted transition-colors">
                    {editImgUploading ? (
                      <RefreshCw className="h-4 w-4 animate-spin" />
                    ) : (
                      <ImagePlus className="h-4 w-4" />
                    )}
                    <span className="text-sm">{editImgUploading ? (t("uploading") || "Uploading...") : (t("uploadImage") || "Upload Image")}</span>
                    <input
                      type="file"
                      accept="image/*"
                      className="hidden"
                      disabled={editImgUploading}
                      onChange={(e) => {
                        const file = e.target.files?.[0];
                        if (file) uploadCategoryImage(file, setEditImgUploading, (url) => setEditFormData(prev => ({ ...prev, icon: url })));
                        e.target.value = '';
                      }}
                    />
                  </label>
                  {editFormData.icon && (
                    <Button variant="ghost" size="sm" onClick={() => setEditFormData(prev => ({ ...prev, icon: '' }))}>
                      {t("remove") || "Remove"}
                    </Button>
                  )}
                </div>
              </div>

              <div className="space-y-2">
                <Label>{t("status") || "Status"}</Label>
                <Select
                  value={editFormData.is_active !== undefined ? editFormData.is_active.toString() : 'true'}
                  onValueChange={(value) => setEditFormData(prev => ({ ...prev, is_active: value === 'true' }))}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="true">{t("active") || "Active"}</SelectItem>
                    <SelectItem value="false">{t("inactive") || "Inactive"}</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          )}
          
          <DialogFooter>
            <Button variant="outline" onClick={() => {
              setIsEditDialogOpen(false);
              setEditFormData({});
              setSelectedCategory(null);
            }}>
              {t("cancel") || "Cancel"}
            </Button>
            <Button
              onClick={handleSaveEdit}
              disabled={updateCategoryMutation.isPending || editImgUploading}
            >
              {updateCategoryMutation.isPending ? (
                <>
                  <RefreshCw className="mr-2 h-4 w-4 animate-spin" />
                  {t("saving") || "Saving..."}
                </>
              ) : (
                <>
                  <Edit className="mr-2 h-4 w-4" />
                  {t("saveChanges") || "Save Changes"}
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
